import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { useColorMode } from '../context/ColorModeContext';

// Token bridge (Phase 1, docs/ui/colour-audit.md §2.9): the global MUI theme
// reads the app tokens (saffron primary/action, basil-neutral secondary).
// Palette entries are RAW HEX mirrors of tokens.css Tier-1
// primitives — MUI's createTheme computes hover/ripple variants at construction
// and cannot resolve a var(). styleOverrides (plain CSS output) use var()/
// color-mix so they follow [data-theme] re-points. Sync is enforced by
// src/__tests__/token-contract.test.ts.
const buildTheme = (mode: 'light' | 'dark') => createTheme({
  palette: mode === 'dark' ? {
    mode: 'dark',
    // Mirrors of tokens.css [data-theme=dark] — sync via token-contract test
    primary:   { main: '#4E9E77', light: '#79BC98', dark: '#5FB088', contrastText: '#0F2C21' },
    secondary: { main: '#8B9484', light: '#B4BDAC', dark: '#5B6553', contrastText: '#12160F' },
    success: { main: '#1E8E5A', dark: '#69C795', light: '#12331F', contrastText: '#EAEFE7' },
    error:   { main: '#D2453C', dark: '#EC8A82', light: '#3A1B18', contrastText: '#EAEFE7' },
    warning: { main: '#E0891E', dark: '#E9B872', light: '#3A2A0F', contrastText: '#EAEFE7' },
    info:    { main: '#2D6CB5', dark: '#8FBBEC', light: '#152A40', contrastText: '#EAEFE7' },
    text: { primary: '#EAEFE7', secondary: '#B4BDAC', disabled: '#5B6553' },
    background: { default: '#12160F', paper: '#1A1F17' },
    divider: '#353E30',
  } : {
    primary: {
      main: '#A66A10',        // saffron (--action)
      light: '#C98A1B',       // saffron (--focus-ring)
      dark: '#8F5A0E',        // saffron (--action-hover)
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#6B7469',        // --n-500 (--text-tertiary)
      light: '#929B8D',       // --n-400
      dark: '#515A50',        // --n-600
      contrastText: '#FFFFFF',
    },
    success: { main: '#1E8E5A', dark: '#10603C', light: '#E6F4EC', contrastText: '#FFFFFF' }, // ok-500/700/100
    error:   { main: '#D2453C', dark: '#9A2E27', light: '#FBEAE8', contrastText: '#FFFFFF' }, // bad-500/700/100
    warning: { main: '#E0891E', dark: '#8A5411', light: '#FCF1E0', contrastText: '#FFFFFF' }, // warn-500/700/100
    info:    { main: '#2D6CB5', dark: '#1E4E86', light: '#E9F1FA', contrastText: '#FFFFFF' }, // info-500/700/100
    text: {
      primary: '#262C26',     // --n-800 (--text-primary)
      secondary: '#515A50',   // --n-600 (--text-secondary)
      disabled: '#929B8D',    // --n-400 (--text-disabled)
    },
    background: {
      default: '#FAFBF9',     // --n-25 (--bg-app)
      paper: '#FFFFFF',       // --n-0 (--bg-surface)
    },
    divider: '#D6DBD3',       // --n-200 (--border-default)
  },
  typography: {
    // Responsive font sizes (clamp uses MIN value on narrow viewports → mobile).
    // Phase 1.5: lowered MIN values ~20% so mobile renders smaller body/heading text.
    fontSize: 14,
    htmlFontSize: 16,
    // Desktop sizes via clamp.
    h1: { fontSize: 'clamp(1.375rem, 5vw + 0.8rem, 2.25rem)',  lineHeight: 1.2,  fontWeight: 700 },
    h2: { fontSize: 'clamp(1.125rem, 4vw + 0.7rem, 1.875rem)', lineHeight: 1.25, fontWeight: 700 },
    h3: { fontSize: 'clamp(1rem, 3.5vw + 0.6rem, 1.5rem)',     lineHeight: 1.3,  fontWeight: 600 },
    h4: { fontSize: 'clamp(0.9375rem, 3vw + 0.5rem, 1.25rem)', lineHeight: 1.4,  fontWeight: 600 },
    h5: { fontSize: 'clamp(0.875rem, 2.5vw + 0.5rem, 1.125rem)', lineHeight: 1.4, fontWeight: 600 },
    h6: { fontSize: 'clamp(0.8125rem, 2vw + 0.5rem, 1rem)',    lineHeight: 1.4,  fontWeight: 600 },
    body1: { fontSize: 'clamp(0.75rem, 2vw + 0.5rem, 1rem)',     lineHeight: 1.5  },
    body2: { fontSize: 'clamp(0.6875rem, 1.5vw + 0.5rem, 0.875rem)', lineHeight: 1.45 },
    button:  { fontSize: 'clamp(0.75rem, 2vw + 0.5rem, 1rem)',  textTransform: 'none' },
    caption: { fontSize: 'clamp(0.625rem, 1vw + 0.5rem, 0.813rem)', lineHeight: 1.4 },
    overline:{ fontSize: 'clamp(0.625rem, 1vw + 0.5rem, 0.813rem)', lineHeight: 1.4,
               textTransform: 'uppercase', letterSpacing: '0.08em' },
  },
  components: {
    // Override all Button components
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '6px',
          '@media (max-width:600px)': {
            minHeight: 40,
            padding: '8px 16px',
            fontSize: '0.875rem',
          },
        },
        contained: {
          backgroundColor: 'var(--action)',
          color: 'var(--text-inverse)',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          '&:hover': {
            backgroundColor: 'var(--action-hover)',
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
          },
        },
        containedPrimary: {
          backgroundColor: 'var(--action)',
          color: 'var(--text-inverse)',
          '&:hover': {
            backgroundColor: 'var(--action-hover)',
          },
        },
        containedSecondary: {
          backgroundColor: 'var(--action)',
          color: 'var(--text-inverse)',
          '&:hover': {
            backgroundColor: 'var(--action-hover)',
          },
        },
        containedError: {
          color: 'var(--text-inverse)',
        },
        containedWarning: {
          color: 'var(--text-inverse)',
        },
        containedInfo: {
          color: 'var(--text-inverse)',
        },
        containedSuccess: {
          color: 'var(--text-inverse)',
        },
        text: {
          color: 'var(--action)',
          '&:hover': {
            backgroundColor: 'color-mix(in srgb, var(--action) 8%, transparent)',
          },
        },
        textPrimary: {
          color: 'var(--action)',
          '&:hover': {
            backgroundColor: 'color-mix(in srgb, var(--action) 8%, transparent)',
          },
        },
        outlined: {
          backgroundColor: 'transparent',
          color: 'var(--action)',
          borderColor: 'var(--action)',
          '&:hover': {
            backgroundColor: 'color-mix(in srgb, var(--action) 8%, transparent)',
            borderColor: 'var(--action-hover)',
          },
        },
        outlinedPrimary: {
          borderColor: 'var(--action)',
          color: 'var(--action)',
          '&:hover': {
            borderColor: 'var(--action-hover)',
            color: 'var(--action-hover)',
          },
        },
        outlinedError: {
          borderColor: 'var(--status-bad)',
          color: 'var(--status-bad)',
        },
        outlinedWarning: {
          borderColor: 'var(--status-warn)',
          color: 'var(--status-warn)',
        },
      },
    },
    // Override all Tab components
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          color: 'var(--text-secondary)',
          '&.Mui-selected': {
            color: 'var(--action)',
          },
          '@media (max-width:600px)': {
            minHeight: 32,
            padding: '4px 8px',
            fontSize: '0.75rem',
          },
        },
      },
    },
    // Override Tabs indicator
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: 'var(--action)',
          height: '2px',
        },
      },
    },
    // Override IconButton components — only set hover, don't force bg/color
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '50%',
          '&:hover': {
            backgroundColor: 'color-mix(in srgb, var(--action) 12%, transparent)',
          },
        },
      },
    },
    // Override Dialog components
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-subtle)',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden',
        },
      },
    },
    // Override DialogTitle components
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          background: 'var(--bg-surface)',
          borderBottom: '1px solid var(--border-subtle)',
          padding: '16px 20px',
          fontSize: '1.1rem',
          fontWeight: 600,
          color: 'var(--text-primary)',
          '@media (max-width:600px)': {
            padding: '8px 11px',
            fontSize: '0.875rem',
          },
        },
      },
    },
    // Override DialogContent components
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px',
          background: 'var(--bg-surface)',
          '@media (max-width:600px)': {
            padding: '10px 11px',
          },
        },
      },
    },
    // Override DialogActions components
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 20px',
          background: 'var(--bg-sunken)',
          borderTop: '1px solid var(--border-subtle)',
          gap: '8px',
          '@media (max-width:600px)': {
            padding: '6px 11px',
            gap: '5px',
            flexWrap: 'wrap',
          },
        },
      },
    },
    // Tighter Card padding on mobile
    MuiCard: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            borderRadius: 6,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '8px 10px',
            '&:last-child': { paddingBottom: '8px' },
          },
        },
      },
    },
    // Tighter list rows on mobile
    MuiListItem: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            paddingTop: 4,
            paddingBottom: 4,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          '@media (max-width:600px)': { fontSize: '0.8125rem' },
        },
        secondary: {
          '@media (max-width:600px)': { fontSize: '0.6875rem' },
        },
      },
    },
    // Form labels & inputs
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': { fontSize: '0.75rem' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': { fontSize: '0.8125rem' },
        },
        input: {
          '@media (max-width:600px)': { padding: '8px 10px' },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': { fontSize: '0.8125rem' },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': { fontSize: '0.625rem', marginTop: 1 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          '@media (max-width:600px)': {
            padding: '4px 6px',
            fontSize: '0.75rem',
          },
        },
        head: {
          '@media (max-width:600px)': { fontSize: '0.6875rem', fontWeight: 700 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-filled': {
            backgroundColor: 'color-mix(in srgb, var(--action) 8%, transparent)',
            color: 'var(--action)',
          },
          '@media (max-width:600px)': {
            height: 18,
            fontSize: '0.625rem',
            '& .MuiChip-label': { padding: '0 6px' },
          },
        },
      },
    },
    // Phase 1.5: hard mobile typography clamps so vw-based clamp() can't
    // grow text beyond these ceilings on narrow viewports.
    MuiTypography: {
      styleOverrides: {
        h1: { '@media (max-width:600px)': { fontSize: '1.25rem !important', lineHeight: 1.2 } },
        h2: { '@media (max-width:600px)': { fontSize: '1.125rem !important', lineHeight: 1.25 } },
        h3: { '@media (max-width:600px)': { fontSize: '1rem !important',     lineHeight: 1.3 } },
        h4: { '@media (max-width:600px)': { fontSize: '0.9375rem !important', lineHeight: 1.35 } },
        h5: { '@media (max-width:600px)': { fontSize: '0.875rem !important', lineHeight: 1.4 } },
        h6: { '@media (max-width:600px)': { fontSize: '0.8125rem !important', lineHeight: 1.4 } },
        body1:   { '@media (max-width:600px)': { fontSize: '0.8125rem !important', lineHeight: 1.45 } },
        body2:   { '@media (max-width:600px)': { fontSize: '0.75rem !important',   lineHeight: 1.4 } },
        caption: { '@media (max-width:600px)': { fontSize: '0.6875rem !important', lineHeight: 1.35 } },
        overline:{ '@media (max-width:600px)': { fontSize: '0.625rem !important',  lineHeight: 1.3 } },
        subtitle1: { '@media (max-width:600px)': { fontSize: '0.875rem !important' } },
        subtitle2: { '@media (max-width:600px)': { fontSize: '0.8125rem !important' } },
      },
    },
    // Override TextField components
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--action)',
            },
          },
        },
      },
    },
    // Override Select components
    MuiSelect: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--action)',
          },
        },
      },
    },
    // Override FormControl components
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root.Mui-focused': {
            color: 'var(--action)',
          },
        },
      },
    },
    // Override Menu components
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    // Override MenuItem components
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'var(--bg-hover)',
          },
          '&.Mui-selected': {
            backgroundColor: 'color-mix(in srgb, var(--action) 8%, transparent)',
            color: 'var(--action)',
            '&:hover': {
              backgroundColor: 'color-mix(in srgb, var(--action) 15%, transparent)',
            },
          },
        },
      },
    },
  },
});

interface GlobalMuiOverridesProps {
  children: React.ReactNode;
}

export const GlobalMuiOverrides: React.FC<GlobalMuiOverridesProps> = ({ children }) => {
  const { mode } = useColorMode();
  const theme = React.useMemo(() => buildTheme(mode), [mode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default GlobalMuiOverrides; 